import React, { useState, useEffect } from 'react';
import { useAuth, useInviteReturnPath } from '../context/AuthContext';
import {
  Box,
  Button,
  VStack,
  Heading,
  Text,
  Flex,
  useToast,
  // PRESERVED FOR FUTURE DISCORD INTEGRATION - DO NOT REMOVE
  // Will be used for Discord profile images in enhanced authentication UI
  Image,
  Divider,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router';
import { theme as mythboundTheme } from '../config/theme';
import OAuthDebugInfo from '../components/OAuthDebugInfo';
import OAuthErrorFeedback from '../components/OAuthErrorFeedback';
import ConfigTest from '../components/ConfigTest';
import { OAuthDiagnostics, OAuthErrorType, parseOAuthError, checkDiscordConfig, checkGoogleConfig } from '../utils/oauthDiagnostics';
import { getApiBaseUrl } from '../utils/environment';
import { GlobalFooter } from '../components/ui/GlobalFooter';

/**
 * Login Component
 * 
 * Implements the authentication page with OAuth integration.
 * Features:
 * - Discord OAuth login button
 * - Google OAuth login button
 */
export const Login: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const {} = useAuth();

  // PRD001A 001A.6 item 3: if the user is already authenticated (e.g. they
  // navigated back to /login directly, or landed here from some other flow)
  // and had a pending invite stored before going to log in, resume it
  // instead of showing the login form again. The normal post-OAuth landing
  // page is Dashboard.tsx (the backend's fixed redirect target), which has
  // its own call to the same hook — this covers this page too, defensively.
  useInviteReturnPath();

  // State for OAuth error handling
  const [oauthError, setOauthError] = useState<OAuthDiagnostics | null>(null);

  // This component only handles the initial login page, not the OAuth callback

  // Check for error parameters in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    const errorDescription = params.get('error_description');
    
    // Listen for auth error events
    const handleAuthError = (event: CustomEvent) => {
      if (event.detail && event.detail.error) {
        setOauthError(event.detail.error);
      }
    };
    
    window.addEventListener('auth:error', handleAuthError as EventListener);
    
    // Handle standard error parameters
    if (error === 'session_expired') {
      toast({
        title: 'Session Expired',
        description: 'Your session has expired. Please log in again.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      
      // Remove the error parameter from the URL to prevent showing the message again on refresh
      navigate('/login', { replace: true });
    } else if (error === 'authentication_required') {
      toast({
        title: 'Authentication Required',
        description: 'Please log in to access this resource.',
        status: 'info',
        duration: 5000,
        isClosable: true,
      });
      
      // Remove the error parameter from the URL
      navigate('/login', { replace: true });
    } else if (error && error !== 'access_denied') {
      // Handle OAuth specific errors
      // access_denied is when user cancels the OAuth flow, so we don't show an error
      const currentUrl = window.location.href;
      const oauthDiagnostics = parseOAuthError(currentUrl) || {
        provider: errorDescription?.includes('google') ? 'google' : 'discord',
        errorType: OAuthErrorType.UNKNOWN_ERROR,
        message: errorDescription || `Authentication error: ${error}`,
        possibleSolutions: [
          'Try again with a different provider',
          'Check that the application is properly configured',
          'Contact support if the issue persists'
        ],
        hasError: true
      };
      
      setOauthError(oauthDiagnostics);
      
      // Remove the error parameter from the URL
      navigate('/login', { replace: true });
    }
    
    // Run configuration check on mount
    const checkConfigurations = async () => {
      try {
        const discordDiagnostics = await checkDiscordConfig();
        const googleDiagnostics = await checkGoogleConfig();
        
        if (discordDiagnostics.hasError && googleDiagnostics.hasError) {
          // If both providers have errors, set the Discord one as it's the primary provider
          setOauthError(discordDiagnostics);
        } else if (discordDiagnostics.hasError) {
          setOauthError(discordDiagnostics);
        } else if (googleDiagnostics.hasError) {
          setOauthError(googleDiagnostics);
        }
      } catch (error) {
        console.error('Failed to check OAuth configurations:', error);
      }
    };
    
    checkConfigurations();
    
    return () => {
      window.removeEventListener('auth:error', handleAuthError as EventListener);
    };
  }, [location, toast, navigate]);

  // Handle Discord OAuth login
  const handleDiscordLogin = () => {
    try {
      const backendApiBaseUrl = getApiBaseUrl().replace('/api', ''); // Remove /api suffix
      const loginUrl = `${backendApiBaseUrl}/auth/login/discord`;
      console.log(`[Login] Navigating to Discord login: ${loginUrl}`);
      window.location.href = loginUrl;
    } catch (error) {
      console.error(`[Login] Failed to navigate to Discord login:`, error);
      toast({
        title: 'Login Error',
        description: 'Failed to initiate Discord login.',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  };

  // Handle Google OAuth login
  const handleGoogleLogin = () => {
    try {
      const backendApiBaseUrl = getApiBaseUrl().replace('/api', ''); // Remove /api suffix
      const loginUrl = `${backendApiBaseUrl}/auth/login/google`;
      console.log(`[Login] Navigating to Google login: ${loginUrl}`);
      window.location.href = loginUrl;
    } catch (error) {
      console.error(`[Login] Failed to navigate to Google login:`, error);
      toast({
        title: 'Login Error',
        description: 'Failed to initiate Google login.',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Flex 
        minHeight="100vh" 
        width="full" 
        align="center" 
        justifyContent="center"
        bg={mythboundTheme.colors.gradients.darkPortal}
      >
        <Box 
          p={8} 
          maxWidth="500px" 
          borderWidth={2}
          borderRadius="md" 
          boxShadow={mythboundTheme.shadows.glow}
          borderColor={mythboundTheme.colors.primary.main}
          bg="rgba(17, 17, 22, 0.9)"
        >
          <Box textAlign="center">
            <Heading 
              fontFamily={mythboundTheme.typography.fontFamily.heading}
              color={mythboundTheme.colors.primary.gold}
              mb={6}
            >
              Mythbound™
            </Heading>
            <Text 
              fontSize="xl" 
              fontFamily={mythboundTheme.typography.fontFamily.heading}
              color={mythboundTheme.colors.secondary.white}
              mb={8}
            >
              Begin Your Adventure
            </Text>
          </Box>
          
          <Box my={4} textAlign="center">
            {oauthError && <OAuthErrorFeedback diagnostics={oauthError} />}
            
            {/* Show warning if in development mode for better debugging */}
            {process.env.NODE_ENV === 'development' && (
              <Alert status="info" mb={4} borderRadius="md" fontSize="sm">
                <AlertIcon />
                <Text fontSize="sm">Development mode: OAuth debug information shown below</Text>
              </Alert>
            )}
            
            <Text color={mythboundTheme.colors.secondary.white} mb={6}>
              Sign in with your account:
            </Text>
            
            <VStack spacing={4} width="full">
              <Button 
                onClick={handleDiscordLogin}
                className="w-full bg-[#5865F2] hover:bg-[#4752C4] text-white font-bold py-3 px-4 rounded-lg transition-colors"
              >
                Login with Discord
              </Button>
              
              <Button 
                onClick={handleGoogleLogin}
                className="w-full bg-[#DB4437] hover:bg-[#C53929] text-white font-bold py-3 px-4 rounded-lg transition-colors"
                _hover={{
                  bg: '#C53929', // Darker Google red
                }}
              >
                Continue with Google
              </Button>
            </VStack>
            
            <Divider my={8} borderColor={mythboundTheme.colors.accent.silver} opacity={0.3} />
            
            <Text color={mythboundTheme.colors.secondary.white} fontSize="sm" mt={4}>
              By signing in, you agree to our{' '}
              <Text as="a" href="/terms" color={mythboundTheme.colors.primary.main} textDecoration="underline">
                Terms of Service
              </Text>{' '}
              and{' '}
              <Text as="a" href="/privacy" color={mythboundTheme.colors.primary.main} textDecoration="underline">
                Privacy Policy
              </Text>
            </Text>
            {/* OAuth Debug Information - helps with troubleshooting */}
            <ConfigTest />
            <OAuthDebugInfo />
          </Box>
        </Box>
      </Flex>
      <Box px={{ base: 4, md: 8 }} pb={6}>
        <GlobalFooter />
      </Box>
    </>
  );
};

export default Login;
